import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChartBar, ClipboardText, EnvelopeSimple, FilePdf, GearSix, Key, PaperPlaneTilt, SignOut, SpinnerGap, Trash, User, Users, Buildings, Wrench } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import StatusBadge from "@/components/StatusBadge";
import AdminServices from "@/components/admin/AdminServices";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminEmails from "@/components/admin/AdminEmails";
import AdminStats from "@/components/admin/AdminStats";
import AdminContacts from "@/components/admin/AdminContacts";
import AdminDocuments from "@/components/admin/AdminDocuments";
import AdminBuildings from "@/components/admin/AdminBuildings";
import AdminAccounts from "@/components/admin/AdminAccounts";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { STATUSES, categoryByKey, formatDate, formatDateShort, formatTime } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

const REPORTS_PAGE_SIZE = 8;

const TABS = [
  { key: "reports", label: "Signalements", Icon: ClipboardText, perm: "reports" },
  { key: "stats", label: "Statistiques", Icon: ChartBar, perm: "reports" },
  { key: "services", label: "Prestataires", Icon: Wrench, perm: "annuaire" },
  { key: "contacts", label: "Contacts", Icon: Users, perm: "contacts" },
  { key: "documents", label: "Documents", Icon: FilePdf, perm: "documents" },
  { key: "buildings", label: "Copropriétés", Icon: Buildings, adminOnly: true },
  { key: "accounts", label: "Accès", Icon: Key, adminOnly: true },
  { key: "emails", label: "Emails", Icon: EnvelopeSimple, roles: ["admin"] },
  { key: "settings", label: "Réglages & QR", Icon: GearSix, roles: ["admin", "concierge"] },
];

export default function AdminDashboard() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const visibleTabs = TABS.filter(
    (t) => user?.role === "admin" || (t.roles || []).includes(user?.role) || (t.perm && can(t.perm))
  );
  const [tab, setTab] = useState(null);
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState("tous");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [exportStatus, setExportStatus] = useState("tous");
  const [relancing, setRelancing] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [armingAddProvider, setArmingAddProvider] = useState(null);
  const [addedProviders, setAddedProviders] = useState({});
  const [pendingReviews, setPendingReviews] = useState(0);

  useEffect(() => {
    if (can("annuaire")) {
      api.get("/admin/reviews").then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setPendingReviews(list.filter((x) => !x.comment_approved).length);
      }).catch(() => {});
    }
  }, [can]);
  const SUGGESTION_MAP = { serrurerie: ["Serrurerie", "serrurerie"], eclairage: ["Électricité", "electricite"], fuite: ["Plomberie", "plomberie"] };
  const addSuggestedProvider = async (r) => {
    const [cat, spec] = SUGGESTION_MAP[r.category] || ["Autre", null];
    try {
      await api.post("/admin/services", {
        category: cat,
        name: r.provider_name,
        description: "",
        phone: "",
        email: null,
        alert_categories: [],
        specialites: spec ? [spec] : [],
      });
      setAddedProviders((m) => ({ ...m, [r.ref]: true }));
      toast.success(`${r.provider_name} ajouté à l'annuaire (pastille ${cat})`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setArmingAddProvider(null);
  };

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/buildings").then((r) => setBuildings(r.data)).catch(() => {});
    }
  }, [user]);

  const switchBuilding = async (bid) => {
    try {
      await api.post(`/admin/buildings/${bid}/activate`);
      toast.success("Copropriété active changée");
      window.location.reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const relanceProvider = async (r) => {
    setRelancing(r.ref);
    try {
      const { data } = await api.post(`/admin/reports/${r.ref}/relance-prestataire`);
      toast.success(`Email renvoyé à : ${data.sent.join(", ")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setRelancing(null);
  };
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  const activeTab = tab && visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0]?.key;

  useEffect(() => {
    if (can("reports")) {
      api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
    }
  }, [user]);

  const counts = (reports || []).reduce(
    (acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }),
    {}
  );

  const filtered = (reports || []).filter((r) => filter === "tous" || r.status === filter);

  const pageCount = Math.max(1, Math.ceil(filtered.length / REPORTS_PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * REPORTS_PAGE_SIZE, page * REPORTS_PAGE_SIZE);

  useEffect(() => {
    setPage((p) => Math.min(p, Math.max(1, Math.ceil(filtered.length / REPORTS_PAGE_SIZE))));
  }, [filtered.length]);

  const exportUrl =
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/export.pdf` +
    (exportStatus !== "tous" || exportFrom || exportTo
      ? "?" +
        [
          exportStatus !== "tous" ? `status=${exportStatus}` : "",
          exportFrom ? `date_from=${exportFrom}` : "",
          exportTo ? `date_to=${exportTo}` : "",
        ]
          .filter(Boolean)
          .join("&")
      : "");

  const setDraft = (ref, patch) => setDrafts((d) => ({ ...d, [ref]: { ...d[ref], ...patch } }));

  const save = async (report) => {
    const draft = drafts[report.ref] || {};
    const status = draft.status || report.status;
    if (status === report.status && !draft.comment) return;
    setSaving(report.ref);
    try {
      const { data } = await api.patch(`/admin/reports/${report.ref}`, {
        status,
        comment: draft.comment || null,
      });
      setReports((rs) => rs.map((r) => (r.ref === report.ref ? data : r)));
      setDrafts((d) => ({ ...d, [report.ref]: {} }));
      toast.success(`${report.ref} mis à jour`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(null);
  };

  const removeReport = async (report) => {
    if (confirmDelete !== report.ref) {
      setConfirmDelete(report.ref);
      window.setTimeout(() => setConfirmDelete((c) => (c === report.ref ? null : c)), 4000);
      return;
    }
    try {
      await api.delete(`/admin/reports/${report.ref}`);
      setReports((rs) => rs.filter((r) => r.ref !== report.ref));
      setConfirmDelete(null);
      toast.success(`${report.ref} supprimé`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-[#F7F5F0]/85 backdrop-blur-xl">
        <div className="mx-auto flex min-h-16 max-w-5xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-2">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/" data-testid="admin-logo-link" aria-label="Retour à l'accueil">
              <Logo size={34} />
            </Link>
            <div className="leading-tight">
              <p className="font-display text-base font-bold tracking-tight">
                {user?.role === "admin" ? "Administration" : user?.name || "Espace délégué"}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          {buildings.length > 1 && (
            <div className="flex items-center gap-2 max-sm:order-3 max-sm:w-full">
              <Buildings size={16} weight="duotone" className="shrink-0 text-primary" />
              <select
                value={buildings.find((b) => b.active)?.id || ""}
                onChange={(e) => switchBuilding(e.target.value)}
                data-testid="admin-building-switcher"
                aria-label="Changer de copropriété"
                className="min-w-0 flex-1 truncate rounded-full border border-black/10 bg-white px-3 py-2 text-xs font-bold outline-none transition-colors focus:border-primary sm:max-w-64 sm:flex-none"
              >
                {buildings.map((b) => (
                  <option key={b.id} value={b.id} data-testid={`switcher-building-${b.id}`}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
          >
            <SignOut size={14} weight="bold" /> Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        {visibleTabs.length === 0 && (
          <div className="rounded-3xl border border-black/5 bg-white p-10 text-center" data-testid="admin-no-access">
            <p className="font-display text-lg font-bold">Aucun espace de gestion pour ce compte</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Le gestionnaire ne vous a accordé aucun droit de gestion. Si vous devez noter un passage
              d'entretien, demandez-lui le droit « Entretien » puis rendez-vous sur la page Entretien.
            </p>
          </div>
        )}

        {visibleTabs.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-tabs">
            {visibleTabs.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition-all duration-200",
                  activeTab === key
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-black/10 bg-white text-foreground/60 hover:border-primary/40 hover:text-foreground"
                )}
              >
                <Icon size={16} weight="duotone" />
                {label}
                {key === "services" && pendingReviews > 0 && (
                  <span
                    data-testid="tab-services-pending-badge"
                    className={cn(
                      "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-extrabold",
                      activeTab === "services" ? "bg-white text-primary" : "bg-[#E8A33D] text-white"
                    )}
                  >
                    {pendingReviews}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {activeTab === "stats" && <AdminStats />}
        {activeTab === "services" && <AdminServices />}
        {activeTab === "contacts" && <AdminContacts />}
        {activeTab === "documents" && <AdminDocuments />}
        {activeTab === "buildings" && <AdminBuildings />}
        {activeTab === "accounts" && <AdminAccounts />}
        {activeTab === "emails" && <AdminEmails />}
        {activeTab === "settings" && <AdminSettings />}

        {activeTab === "reports" && (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-3xl border border-black/5 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="export-panel">
              <span className="text-xs font-bold text-muted-foreground">Export PDF :</span>
              <select
                value={exportStatus}
                onChange={(e) => setExportStatus(e.target.value)}
                data-testid="export-status-select"
                className="rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold outline-none transition-colors focus:border-primary"
              >
                <option value="tous">Tous les statuts</option>
                {Object.entries(STATUSES).map(([key, s]) => (
                  <option key={key} value={key} data-testid={`export-status-${key}`}>{s.label}</option>
                ))}
              </select>
              <input
                type="date"
                value={exportFrom}
                onChange={(e) => setExportFrom(e.target.value)}
                data-testid="export-date-from"
                aria-label="Date de début"
                className="rounded-full border border-black/10 px-4 py-2 text-xs font-semibold outline-none transition-colors focus:border-primary"
              />
              <input
                type="date"
                value={exportTo}
                onChange={(e) => setExportTo(e.target.value)}
                data-testid="export-date-to"
                aria-label="Date de fin"
                className="rounded-full border border-black/10 px-4 py-2 text-xs font-semibold outline-none transition-colors focus:border-primary"
              />
              <a
                href={exportUrl}
                data-testid="admin-export-pdf"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
              >
                <FilePdf size={16} weight="duotone" />
                Exporter le récapitulatif PDF
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats/export.pdf`}
                data-testid="admin-export-stats-pdf"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-white px-5 py-2 text-xs font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                <ChartBar size={16} weight="duotone" />
                Exporter les statistiques PDF
              </a>
              <span className="ml-auto text-xs font-semibold text-muted-foreground">
                {(reports || []).length} signalement(s) au total
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-stats">
              {Object.entries(STATUSES).map(([key, s]) => (
                <button
                  key={key}
                  onClick={() => { setFilter(filter === key ? "tous" : key); setPage(1); }}
                  data-testid={`stat-${key}`}
                  className={cn(
                    "rounded-3xl border bg-white p-4 text-left transition-all duration-200",
                    filter === key ? "border-primary ring-2 ring-primary/20" : "border-black/5 hover:border-primary/30"
                  )}
                >
                  <p className="font-display text-3xl font-extrabold" style={{ color: s.text }}>{counts[key] || 0}</p>
                  <p className="mt-1 text-xs font-bold text-muted-foreground">{s.label}</p>
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-4">
              {reports === null && <div className="h-32 animate-pulse rounded-3xl bg-black/5" />}
              {reports !== null && filtered.length === 0 && (
                <p className="rounded-3xl border border-black/5 bg-white p-10 text-center text-sm text-muted-foreground" data-testid="admin-empty">
                  Aucun signalement dans cette catégorie.
                </p>
              )}
              {pageItems.map((r, i) => {
                const { Icon, label } = categoryByKey(r.category);
                const draft = drafts[r.ref] || {};
                const currentStatus = draft.status || r.status;
                const dirty = currentStatus !== r.status || !!draft.comment;
                const confirming = confirmDelete === r.ref;
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
                    className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    data-testid={`admin-report-${r.ref}`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Icon size={20} weight="duotone" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-display text-base font-bold">{label}</p>
                          {r.issue && (
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-bold text-primary" data-testid={`admin-report-issue-${r.ref}`}>
                              {r.issue}
                            </span>
                          )}
                          {r.urgent && (
                            <span className="inline-flex items-center rounded-full bg-[#C8102E]/10 px-2.5 py-0.5 text-[11px] font-bold text-[#C8102E]" data-testid={`admin-report-urgent-${r.ref}`}>
                              Urgent
                            </span>
                          )}
                          <span className="font-mono text-xs font-bold text-muted-foreground">{r.ref} - {formatDateShort(r.created_at)}</span>
                          <StatusBadge status={r.status} />
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {r.location}
                          {r.sub_building ? ` · ${r.sub_building}` : ""}
                          {r.location_detail ? ` — ${r.location_detail}` : ""} · {formatTime(r.created_at)}
                        </p>
                        {r.provider_name && (
                          <p className="mt-0.5 text-xs font-bold text-primary" data-testid={`admin-report-provider-${r.ref}`}>
                            {r.provider_suggested ? "Prestataire suggéré par le résident" : "Prestataire choisi par le résident"} : {r.provider_name}
                          </p>
                        )}
                        {r.provider_suggested && r.provider_name && !addedProviders[r.ref] && (
                          <button
                            onClick={() => (armingAddProvider === r.ref ? addSuggestedProvider(r) : setArmingAddProvider(r.ref))}
                            data-testid={`admin-add-provider-${r.ref}`}
                            className={cn(
                              "mt-1 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold transition-colors",
                              armingAddProvider === r.ref
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-black/10 text-foreground/50 hover:border-primary hover:text-primary"
                            )}
                          >
                            {armingAddProvider === r.ref ? "Confirmer l'ajout à l'annuaire ?" : "+ Ajouter à l'annuaire"}
                          </button>
                        )}
                      </div>
                      <div className="flex w-full gap-2 sm:w-auto">
                        <button
                          onClick={() => relanceProvider(r)}
                          disabled={relancing === r.ref}
                          data-testid={`admin-relance-${r.ref}`}
                          title="Renvoyer l'email au prestataire (avec le lien de suivi)"
                          className="inline-flex flex-1 shrink-0 items-center justify-center gap-2 rounded-full border border-black/10 px-4 py-2 text-xs font-bold text-foreground/50 transition-colors hover:border-primary hover:text-primary disabled:opacity-60 sm:flex-none"
                        >
                          {relancing === r.ref ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="duotone" />}
                          Relancer
                        </button>
                        <button
                          onClick={() => removeReport(r)}
                          data-testid={`admin-delete-${r.ref}`}
                          className={cn(
                            "inline-flex flex-1 shrink-0 items-center justify-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition-all duration-200 sm:flex-none",
                            confirming
                              ? "border-[#C90A0A] bg-[#C90A0A] text-white"
                              : "border-black/10 text-foreground/50 hover:border-[#C90A0A] hover:text-[#C90A0A]"
                          )}
                        >
                          <Trash size={14} weight="duotone" />
                          {confirming ? "Confirmer ?" : "Supprimer"}
                        </button>
                      </div>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-foreground/80">{r.description}</p>
                    {r.last_relance_at && (
                      <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-bold text-primary" data-testid={`admin-relance-info-${r.ref}`}>
                        <PaperPlaneTilt size={11} weight="fill" />
                        Relance envoyée le {formatDateShort(r.last_relance_at)} à {formatTime(r.last_relance_at)}{r.last_relance_to?.length ? ` · ${r.last_relance_to.join(", ")}` : ""}
                      </p>
                    )}

                    {(r.contact_name || r.contact_phone || r.contact_email) && (
                      <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-secondary-foreground" data-testid={`admin-contact-${r.ref}`}>
                        <User size={13} weight="fill" />
                        {[r.contact_name || "Résident", r.contact_phone, r.contact_email].filter(Boolean).join(" · ")}
                      </p>
                    )}

                    {r.photos?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {r.photos.map((p, j) => (
                          <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer">
                            <img src={fileUrl(p)} alt={`Photo ${j + 1}`} className="h-16 w-16 rounded-xl border border-black/5 object-cover" />
                          </a>
                        ))}
                      </div>
                    )}

                    <div className="mt-5 flex flex-col gap-3 border-t border-black/5 pt-5 sm:flex-row sm:items-center">
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(STATUSES).map(([key, s]) => (
                          <button
                            key={key}
                            onClick={() => setDraft(r.ref, { status: key })}
                            data-testid={`admin-status-${key}-${r.ref}`}
                            className={cn(
                              "rounded-full border px-4 py-2 text-xs font-bold transition-all duration-200",
                              currentStatus === key ? "border-transparent" : "border-black/10 hover:border-black/25"
                            )}
                            style={currentStatus === key ? { backgroundColor: s.bg, color: s.text } : {}}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-1 gap-2 max-sm:flex-col">
                        <input
                          type="text"
                          value={draft.comment || ""}
                          onChange={(e) => setDraft(r.ref, { comment: e.target.value })}
                          placeholder="Commentaire pour les résidents (optionnel)"
                          data-testid={`admin-comment-input-${r.ref}`}
                          className="flex-1 rounded-full border border-black/10 px-4 py-2 text-xs outline-none transition-colors focus:border-primary"
                        />
                        <button
                          onClick={() => save(r)}
                          disabled={!dirty || saving === r.ref}
                          data-testid={`admin-save-${r.ref}`}
                          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:cursor-not-allowed disabled:opacity-40 max-sm:w-full"
                        >
                          {saving === r.ref ? <SpinnerGap size={14} className="animate-spin" /> : null}
                          Mettre à jour
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {pageCount > 1 && (
              <div className="mt-6 flex items-center justify-center gap-3" data-testid="reports-pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="reports-prev-page"
                  className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-bold transition-colors hover:border-primary hover:text-primary disabled:opacity-40"
                >
                  ← Précédent
                </button>
                <span className="text-xs font-bold text-muted-foreground" data-testid="reports-page-info">
                  Page {page} / {pageCount}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={page === pageCount}
                  data-testid="reports-next-page"
                  className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-bold transition-colors hover:border-primary hover:text-primary disabled:opacity-40"
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
